import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Plus, Pencil, Trash2, Check, X, Folder, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export default function FolderBar({ folders, activeFolder, onSelect }) {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["folders"] });

  const createMutation = useMutation({
    mutationFn: (name) => api.post("/folders", { name }),
    onSuccess: () => {
      invalidate();
      setCreating(false);
      setNewName("");
      toast.success("Папка создана");
    },
    onError: () => toast.error("Не удалось создать папку"),
  });

  const renameMutation = useMutation({
    mutationFn: ({ id, name }) => api.patch(`/folders/${id}`, { name }),
    onSuccess: () => {
      invalidate();
      setEditingId(null);
    },
    onError: () => toast.error("Не удалось переименовать папку"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/folders/${id}`),
    onSuccess: (_, deletedId) => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ["videos"] });
      if (activeFolder === deletedId) onSelect("all");
      toast.success("Папка удалена");
    },
    onError: () => toast.error("Не удалось удалить папку"),
  });

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="folder-bar">
      <button
        onClick={() => onSelect("all")}
        className={cn(
          "text-sm px-3 py-1.5 rounded-full border transition-colors",
          activeFolder === "all" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-accent"
        )}
        data-testid="folder-chip-all"
      >
        Все видео
      </button>
      <button
        onClick={() => onSelect("none")}
        className={cn(
          "text-sm px-3 py-1.5 rounded-full border transition-colors",
          activeFolder === "none" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-accent"
        )}
        data-testid="folder-chip-none"
      >
        Без папки
      </button>

      {folders.map((f) => (
        <div key={f.id} className="flex items-center gap-1">
          {editingId === f.id ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (editName.trim()) renameMutation.mutate({ id: f.id, name: editName.trim() });
              }}
              className="flex items-center gap-1"
            >
              <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-8 w-32" autoFocus data-testid={`folder-rename-input-${f.id}`} />
              <button type="submit" className="p-1 text-emerald-600" data-testid={`folder-rename-confirm-${f.id}`}><Check className="w-4 h-4" /></button>
              <button type="button" onClick={() => setEditingId(null)} className="p-1 text-muted-foreground" data-testid={`folder-rename-cancel-${f.id}`}><X className="w-4 h-4" /></button>
            </form>
          ) : (
            <button
              onClick={() => onSelect(f.id)}
              className={cn(
                "flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border transition-colors",
                activeFolder === f.id ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-accent"
              )}
              data-testid={`folder-chip-${f.id}`}
            >
              {activeFolder === f.id ? <FolderOpen className="w-3.5 h-3.5" /> : <Folder className="w-3.5 h-3.5" />}
              {f.name} <span className="opacity-70">({f.video_count})</span>
            </button>
          )}
          {activeFolder === f.id && editingId !== f.id && (
            <>
              <button onClick={() => { setEditingId(f.id); setEditName(f.name); }} className="p-1 text-muted-foreground hover:text-foreground" data-testid={`folder-edit-button-${f.id}`}>
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => deleteMutation.mutate(f.id)} className="p-1 text-muted-foreground hover:text-destructive" data-testid={`folder-delete-button-${f.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>
      ))}

      {creating ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (newName.trim()) createMutation.mutate(newName.trim());
          }}
          className="flex items-center gap-1"
        >
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Название папки" className="h-8 w-36" autoFocus data-testid="new-folder-input" />
          <button type="submit" className="p-1 text-emerald-600" data-testid="new-folder-confirm-button"><Check className="w-4 h-4" /></button>
          <button type="button" onClick={() => setCreating(false)} className="p-1 text-muted-foreground" data-testid="new-folder-cancel-button"><X className="w-4 h-4" /></button>
        </form>
      ) : (
        <button
          onClick={() => setCreating(true)}
          className="flex items-center gap-1 text-sm px-3 py-1.5 rounded-full border border-dashed border-border text-muted-foreground hover:text-foreground hover:border-foreground transition-colors"
          data-testid="new-folder-button"
        >
          <Plus className="w-3.5 h-3.5" /> Новая папка
        </button>
      )}
    </div>
  );
}
